import { useQueryClient } from '@tanstack/react-query';
import isNil from 'lodash-es/isNil';
import serviceToTQKey from 'services/tq-adapter/serviceToTQKey';
import { OneService, OneServiceNoParams, OneServiceWithParams } from 'types/services/services';

/**
 * Provide the ability to invalidate a service.
 * @returns invalidateQuery function to invalidate a service.
 */
const useInvalidateService = () => {
  const queryClient = useQueryClient();

  /**
   * Invalidate the cache data of the service without params.
   * @param service the service without params to invalidate
   */
  function invalidateQuery<Data>(service: OneServiceNoParams<Data>): Promise<void>;
  /**
   * Invalidate the cache data of the service with params.
   * @param service the service with params to invalidate
   * @param params params of the service
   */
  function invalidateQuery<Data, Params extends object>(service: OneServiceWithParams<Data, Params>, params: Params): Promise<void>;
  /**
   * Invalidate the cache data of the service.
   * @param service the service to invalidate
   * @param params params of the service
   */
  function invalidateQuery<Data, Params extends object>(service: OneService<Data, Params>, params?: Params) {
    const queryKey = isNil(params)
      ? serviceToTQKey(service as OneServiceNoParams<Data>)
      : serviceToTQKey(service as OneServiceWithParams<Data, Params>, params);

    return queryClient.invalidateQueries({ queryKey });
  }

  return invalidateQuery;
};

export default useInvalidateService;
